import { useEffect, useMemo, useState } from 'react';
import { Box, Button, Container, Group, Paper, Stack, Text, ThemeIcon, Title, createStyles, useMantineTheme } from '@mantine/core';
import { Check } from 'tabler-icons-react';
import { endpoint } from '@root/Services/Router/EndpointRegistry';
import { useNav } from '@root/Services/NavigationService';
import { useDi } from '@root/Services/DI';
import { BasicRouteLoader } from '@root/Services/Router/BasicRouteLoader';

type PlatformParam = 'azure' | 'gcp' | '';

export function ConnectionConsentSuccess() {
    const { classes } = useStyles();
    const routeLoader = useDi(BasicRouteLoader);
    const theme = useMantineTheme();
    const nav = useNav();
    const canSelfClose = window.opener && !window.opener.closed;
    const [platform, setPlatform] = useState<PlatformParam>('');

    useEffect(() => {
        try {
            const { platform: p } = nav.getData('platform');
            const normalized = (p ?? '').toLowerCase();
            setPlatform(normalized === 'gcp' ? 'gcp' : 'azure');
        } catch {
            setPlatform('');
        }
    }, [nav]);

    return (
        <Box className={classes.root}>
            <Container size={500}>
                <Paper withBorder radius="xl" className={classes.card}>
                    <div className={classes.accentBar} />
                    <Stack align="center" spacing="md">
                        <ThemeIcon size={80} radius="xl" variant="light" color={theme.primaryColor}>
                            <Check size={42} />
                        </ThemeIcon>
                        <Title order={2} className={classes.title}>
                            Success
                        </Title>
                        <Text align="center" size="sm" className={classes.message}>
                            {platform === 'azure' ? 'Azure' : 'Google Cloud'} has granted access. You may close this window and return to the
                            connections wizard.
                        </Text>
                        {!canSelfClose ? null : (
                            <Button variant="light" color="primary" onClick={() => window.close()} data-atid="SuccessClose">
                                Close Window
                            </Button>
                        )}
                    </Stack>
                </Paper>
            </Container>
        </Box>
    );
}
endpoint('_connection-consent-success', ConnectionConsentSuccess, 'Connection Successful');

const useStyles = createStyles((theme) => {
    const primary = theme.colors[theme.primaryColor][6];
    const primaryLight = theme.colors[theme.primaryColor][3];

    return {
        root: {
            minHeight: '100vh',
            backgroundColor: theme.colors.gray[0],
            display: 'flex',
            alignItems: 'center',
        },
        card: {
            position: 'relative',
            borderRadius: 16,
            border: `1px solid ${theme.colors.gray[3]}`,
            backgroundColor: theme.white,
            boxShadow: theme.shadows.md,
            padding: theme.spacing.xl * 1.25,
        },
        accentBar: {
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: 6,
            borderTopLeftRadius: 16,
            borderTopRightRadius: 16,
            backgroundImage: `linear-gradient(90deg, ${primary} 0%, ${primaryLight} 100%)`,
        },
        title: { color: theme.colors.gray[9], letterSpacing: 0.2 },
        message: { color: theme.colors.gray[6] },
    };
});
